import { Location } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { LocalStorageService } from 'angular-2-local-storage';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TableRow } from '../../../services/bb-table.service';
import { SapiService, SapiEntity } from '../../../services/sapi.service';
import { OverlayService } from '../../../services/overlay.service';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { Adunit } from '../adunit/adunit-list.component';
import { LineItem } from '../lineitem/lineitem-details.component';
import { NgForm } from '../../../../node_modules/@angular/forms';
import { Observable, forkJoin } from 'rxjs';
import { Playout } from '../../../interfaces/playout';
import { DefaultService } from '../../../services/default.service';
import { StatisticsService } from '../../../services/statistics.service';

@Component({
	selector: 'ad-position-details',
	templateUrl: './ad-position-details.component.html',
	styleUrls: ['./ad-position-details.component.scss']
})

export class AdPositionDetailsComponent extends AbstractDetailsComponent {

	@ViewChild('formRef', { static: true }) form: NgForm;

	types: string[];
	playout: Playout;

	putRight = false;
	deleteRight = false;

	public useVMAP: boolean;
	public vmapAdunit: Adunit;
	public placeholder: string;
	public lineitemPlaceholder: string;
	public unlink: boolean;

	public assignedAdunits: Adunit[];
	public assignedPrerolls: Adunit[];
	public assignedPostrolls: Adunit[];

	public adunitQuickedits: boolean[];

	adunitFilterSets: [type: string][];

	adunitRows: TableRow[];
	adunitOriginalRows: TableRow[];
	lineitemIds: string[];
	lineitemRows: TableRow[][];

	adunitDefault: any;
	lineitemDefault: any;

	constructor(
		protected sapiService: SapiService,
		protected router: Router,
		protected localStorageService: LocalStorageService,
		protected overlayService: OverlayService,
		protected translate: TranslateService,
		protected route: ActivatedRoute,
		protected defaultService: DefaultService,
		/* Yes this is called the statistics service, the service describes no longer being needed when we upgrade to Angular 7.2 or higher */
		protected statisticsService: StatisticsService,
		protected location: Location) {

		super(route, location);

		this.entityConfiguration = {
			name: 'playout',
			basePath: '/adservices/position/playout',
			parentPath: '/adservices/positions',
			entityTranslation: 'playout',
		};

		this.placeholder = this.translate.instant('ASSIGN_AD_UNIT');
		this.lineitemPlaceholder = this.translate.instant('ASSIGN_LINE_ITEM');
		this.useVMAP = false;
		this.adunitRows = [];
		this.adunitOriginalRows = [];
		this.adunitFilterSets = [];
		this.unlink = true;

		this.adunitQuickedits = [];
		this.lineitemRows = [];
		this.lineitemIds = [];
		this.types = ['PREROLL', 'POSTROLL'];

		this.types.forEach((type: string) => {
			this.lineitemIds[type] = '';
			this.adunitFilterSets[type] = [{
				filters: [{
					type: 'adunit',
					field: 'positionType',
					operator: 'is',
					value: type.toLowerCase()
				}]
			}];
		});

		this.adunitDefault = require('../../../resources/defaults/adunit.json');
		this.lineitemDefault = require('../../../resources/defaults/lineitem.json');

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.authenticationService.hasPermission('delete').subscribe(hasRight => this.deleteRight = hasRight);

		this.entityLoaded.subscribe(() => {
			this.playout = this.entity as Playout;
			this.statisticsService.data = { playoutCode: this.playout.label };
			if (this.playout) {
				this.useVMAP = false;
				let adunitIds: string[] = [];
				if (this.playout.adunitsVMAP && this.playout.adunitsVMAP.length) {
					adunitIds = adunitIds.concat(this.playout.adunitsVMAP.map((vmapAdunit: Adunit) => vmapAdunit.id));
					this.vmapAdunit = this.playout.adunitsVMAP[0];
					this.useVMAP = true;
				}

				this.adunitRows = [];
				if (this.playout.adunitsPreroll && this.playout.adunitsPreroll.length) {
					adunitIds = adunitIds.concat(this.playout.adunitsPreroll.map((prerollAdunit: Adunit) => prerollAdunit.id));
					this.getAdunitAndLineitems('PREROLL', this.playout.adunitsPreroll[0].id, true);
				}
				if (this.playout.adunitsPostroll && this.playout.adunitsPostroll.length) {
					adunitIds = adunitIds.concat(this.playout.adunitsPostroll.map((postrollAdunit: Adunit) => postrollAdunit.id));
					this.getAdunitAndLineitems('POSTROLL', this.playout.adunitsPostroll[0].id, true);
				}
				if (this.playout.adunits && this.playout.adunits.length) {
					const overlays: Adunit[] = this.playout.adunits.filter((overlayAdunit) => adunitIds.indexOf(overlayAdunit.id) < 0);
					if (overlays && overlays.length) {
						this.getAdunitAndLineitems('OVERLAY', overlays[0].id, true);
					}
				}

			}
		});
	}

	getAdunitAndLineitems(adunitType: string, adunitId: string, first = false) {
		this.getAdunit(adunitId).subscribe((adunitEntity: SapiEntity) => {
			const ad: Adunit = adunitEntity as Adunit;
			const playout: Playout = this.entity as Playout;
			ad.playoutCode = playout.label;
			this.adunitRows[adunitType] = { id: ad.id, quickEditing: false, data: ad };
			if (first) {
				this.adunitOriginalRows[adunitType] = { ...ad };
			}
			this.lineitemRows[adunitType] = [];
			const lineitemCalls = Array<Observable<SapiEntity>>();
			ad.lineitems.forEach((lineitem: LineItem) => {
				lineitemCalls.push(this.getLineitem(lineitem.id));
			});
			forkJoin(lineitemCalls).subscribe((result: SapiEntity[]) => {
				if (result) {
					result.forEach((lineitemEntity: SapiEntity) => {
						this.lineitemRows[adunitType].push({
							id: lineitemEntity.id,
							quickEditing: false,
							data: lineitemEntity
						});
					});
				}
				if (!first) {
					this.form.form.markAsDirty();
				}
			});
		});
	}

	getAdunit(adunitId: string) {
		return this.sapiService.get('adunit', adunitId);
	}

	getLineitem(lineitemId: string) {
		return this.sapiService.get('lineitem', lineitemId);
	}

	getVastUrl(vastUrl: string) {
		return encodeURIComponent(vastUrl);
	}

	createAdunit(adunitType: string) {
		const adunit: Adunit = { ...this.adunitDefault };
		adunit.positionType = adunitType.toLowerCase();
		adunit.playout = this.defaultService.getAdunitPlayoutDefault(adunit.positionType);
		this.adunitSelected(adunitType, adunit);
	}

	adunitSelected(adunitType: string, adunit: Adunit) {
		const playout: Playout = this.entity as Playout;
		if (adunit) {
			if (adunit.id) {
				this.getAdunitAndLineitems(adunitType, adunit.id);
			} else {
				adunit.playoutCode = playout.label;
				this.adunitRows[adunitType] = { id: adunit.id, quickEditing: true, data: adunit };
				this.form.form.markAsDirty();
			}
		}
	}

	adunitRemoved(adunitType: string) {
		if (this.adunitRows[adunitType]) {
			this.adunitRows[adunitType] = null;
		}
		if (this.lineitemRows) {
			this.lineitemRows[adunitType] = [];
		}
		this.form.form.markAsDirty();
	}

	createLineitem(adunitType: string) {
		const lineitem: LineItem = { ...this.lineitemDefault };
		this.lineitemSelected(adunitType, lineitem);
	}

	lineitemSelected(adunitType: string, lineitem: LineItem) {
		this.lineitemIds[adunitType] = '';
		if (!this.lineitemRows[adunitType]) {
			this.lineitemRows[adunitType] = [];
		}
		if (lineitem && lineitem.id) {
			this.getLineitem(lineitem.id).subscribe((sapiEntity: SapiEntity) => {
				if (sapiEntity) {
					this.lineitemIds[adunitType] = null;
					this.lineitemRows[adunitType].push({ id: sapiEntity.id, quickEditing: false, data: sapiEntity });
				}
			});
		} else {
			this.lineitemRows[adunitType].push({ id: lineitem.id, quickEditing: true, data: lineitem });
		}
	}

	lineitemCancel(adunitType: string, lineitem: any, index: number) {
		if (lineitem && lineitem.id) {
			lineitem.quickEditing = false;
		} else {
			this.lineitemRemoved(adunitType, lineitem.id, index);
		}
	}

	lineitemRemoved(adunitType: string, lineitemId: string, index: number) {
		if (this.lineitemRows[adunitType] && this.lineitemRows[adunitType].length) {
			if (lineitemId) {
				this.lineitemRows[adunitType] = this.lineitemRows[adunitType].filter((lineitem: LineItem) => lineitem.id !== lineitemId);
			} else {
				this.lineitemRows[adunitType].splice(index, 1);
			}
		}
		this.form.form.markAsDirty();
	}

	save(force?: boolean, extraHeaders?: any, saveDirectly: boolean = true, publish?: boolean): Observable<any> {
		if (this.form.invalid) {
			this.overlayService.createToast(this.translate.instant('UNABLE_TO_SAVE', { type: this.translate.instant('POSITION') }), 'warning');
			return;
		}
		const playout: Playout = this.entity as Playout;

		playout.adunits = [];
		this.types.forEach((type: string) => {
			if (this.adunitRows[type]) {
				const adunit: Adunit = this.adunitRows[type].data;

				switch (type) {
					case 'PREROLL': playout.adunitsPreroll = [adunit];
						break;
					case 'POSTROLL': playout.adunitsPostroll = [adunit];
						break;
				}

				adunit.lineitems = [];
				adunit.assignedLineitems = [];
				if (this.adunitOriginalRows[type] && adunit.id !== this.adunitOriginalRows[type].id) {
					adunit.playoutCode = playout.label;
				}
				if (this.lineitemRows[type] && this.lineitemRows[type].length) {
					this.lineitemRows[type].forEach((lineitemRow: TableRow) => {
						const lineitem: LineItem = lineitemRow.data;
						lineitemRow.quickEditing = false;
						adunit.lineitems.push(lineitem);
						adunit.assignedLineitems.push(lineitem.id);
					});
				}

				playout.adunits.push(adunit);
			}
		});
		if (playout.adunitsVMAP && playout.adunitsVMAP.length) {
			playout.adunits.push(playout.adunitsVMAP[0]);
		}

		return super.save(force, extraHeaders, saveDirectly, publish);
	}


}
